import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetAppointmentAggregatesIO } from '../types';

export const getGetAppointmentAggregatesQueryKey = (req: GetAppointmentAggregatesIO['input']) =>
  ['AnalyticsGatewayV1', 'GetAppointmentAggregates', req] as const;
type QueryKey = ReturnType<typeof getGetAppointmentAggregatesQueryKey>;

/**
 * A hook that returns a query for the `GetAppointmentAggregates` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentAggregatesQuery = <E = unknown, D = GetAppointmentAggregatesIO['output']>(
  req: GetAppointmentAggregatesIO['input'],
  options?: LimitedSchemaQueryOptions<GetAppointmentAggregatesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetAppointmentAggregatesIO['output'], E, D, QueryKey>({
    queryKey: getGetAppointmentAggregatesQueryKey(req),
    queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentAggregates(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetAppointmentAggregates` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetAppointmentAggregatesQueries = <E = unknown, D = GetAppointmentAggregatesIO['output']>(
  argsArr: {
    req: GetAppointmentAggregatesIO['input'];
    options?: LimitedSchemaQueryOptions<GetAppointmentAggregatesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetAppointmentAggregatesQueryKey(req),
      queryFn: () => SchemaAnalyticsGateway1Service.GetAppointmentAggregates(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetAppointmentAggregatesQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetAppointmentAggregates` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetAppointmentAggregates = () => {
  const queryClient = useQueryClient();

  const fetchGetAppointmentAggregates = async <E = unknown, D = GetAppointmentAggregatesIO['output']>(
    req: GetAppointmentAggregatesIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetAppointmentAggregatesIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetAppointmentAggregatesIO['output'], E, D, QueryKey>(
      getGetAppointmentAggregatesQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetAppointmentAggregates(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetAppointmentAggregates;
};
